import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Clock, MapPin, ShieldAlert } from "lucide-react";
import { DashboardLayout, Panel, StatusBadge } from "@/components/DashboardLayout";
import { Avatar } from "@/components/Avatar";
import { InfoTip } from "@/components/InfoTip";
import { useTaxonomy } from "@/lib/useTaxonomy";
import { RatingSummary, Stars } from "@/components/Ratings";
import { api, apiError, naira } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const NAV = [
  { to: "/dashboard/master-client", label: "Overview", testId: "nav-overview" },
  { to: "/beneficiaries", label: "Beneficiaries", testId: "nav-beneficiaries" },
  { to: "/witnesses", label: "Witnesses", testId: "nav-witnesses" },
  { to: "/marketplace", label: "Find providers", testId: "nav-marketplace" },
  { to: "/bookings", label: "Bookings", testId: "nav-bookings" },
];

export function Marketplace() {
  const { user } = useAuth();
  const [filters, setFilters] = useState({ q: "", profession: "", specialty: "", state: "", max_fee: "", min_rating: "", sort: "rating", accepting_only: false });
  const [meta, setMeta] = useState({ categories: [], states: [] });
  const taxonomy = useTaxonomy();
  const selectedProfession = taxonomy.find((p) => p.key === filters.profession);
  const selectedSpecialty = selectedProfession?.specialties.find((s) => s.name === filters.specialty);
  const [data, setData] = useState(null);
  const [blocked, setBlocked] = useState(null);

  const load = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v !== false));
      const { data: res } = await api.get("/api/marketplace/providers", { params });
      setData(res);
      setBlocked(null);
    } catch (e) {
      setBlocked(apiError(e));
      setData({ count: 0, providers: [] });
    }
  };

  useEffect(() => {
    api.get("/api/marketplace/categories").then((r) => setMeta(r.data)).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isClient = ["MASTER_CLIENT", "REFERRAL_CLIENT"].includes(user?.role);

  return (
    <DashboardLayout
      title="Verified healthcare providers"
      subtitle="Only professionals whose licence and identity have been approved appear here."
      nav={isClient ? NAV : []}
    >
      {blocked ? (
        <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-6" data-testid="marketplace-blocked">
          <ShieldAlert className="h-6 w-6 text-amber-700" />
          <div className="min-w-0 flex-1">
            <p className="font-display font-bold text-amber-900">Marketplace locked</p>
            <p className="mt-1 text-sm text-amber-800">{blocked}</p>
          </div>
          {user?.role === "MASTER_CLIENT" && (
            <Button asChild className="rounded-full bg-emerald-900">
              <Link to="/onboarding/master-client" data-testid="marketplace-kyc-cta">Complete KYC</Link>
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-4">
          <aside className="lg:col-span-1">
            <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm lg:sticky lg:top-24" data-testid="marketplace-filters">
              <h2 className="font-display font-bold text-slate-900">Filters</h2>
              <div className="mt-4 space-y-4">
                <div>
                  <Label>Search</Label>
                  <Input className="mt-1.5" placeholder="Name or specialty" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="filter-search-input" />
                </div>
                <div>
                  <Label>Profession</Label>
                  <select
                    className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={filters.profession}
                    onChange={(e) => setFilters({ ...filters, profession: e.target.value, specialty: "" })}
                    data-testid="filter-profession-select"
                  >
                    <option value="">All professions</option>
                    {taxonomy.map((p) => (
                      <option key={p.key} value={p.key}>{p.label}</option>
                    ))}
                  </select>
                  {selectedProfession && (
                    <p className="mt-1.5 flex items-start gap-1.5 text-xs text-slate-500" data-testid="filter-profession-hint">
                      <span>{selectedProfession.headline}</span>
                      <InfoTip headline={selectedProfession.headline} summary={selectedProfession.summary} testId="filter-profession-info" />
                    </p>
                  )}
                </div>
                <div>
                  <Label>Specialty</Label>
                  <select
                    className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={filters.specialty}
                    disabled={!selectedProfession}
                    onChange={(e) => setFilters({ ...filters, specialty: e.target.value })}
                    data-testid="filter-specialty-select"
                  >
                    <option value="">{selectedProfession ? "All specialties" : "Choose a profession first"}</option>
                    {(selectedProfession?.specialties || []).map((s) => (
                      <option key={s.name} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                  {selectedSpecialty && (
                    <p className="mt-1.5 text-xs text-slate-500" data-testid="filter-specialty-hint">{selectedSpecialty.summary}</p>
                  )}
                </div>
                <div>
                  <Label>State</Label>
                  <select className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} data-testid="filter-state-select">
                    <option value="">All states</option>
                    {meta.states.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Maximum fee (₦)</Label>
                  <Input className="mt-1.5" type="number" value={filters.max_fee} onChange={(e) => setFilters({ ...filters, max_fee: e.target.value })} data-testid="filter-maxfee-input" />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={filters.accepting_only} onChange={(e) => setFilters({ ...filters, accepting_only: e.target.checked })} className="accent-emerald-700" data-testid="filter-accepting-checkbox" />
                  Accepting bookings only
                </label>
                <div>
                  <Label>Minimum rating</Label>
                  <select className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={filters.min_rating} onChange={(e) => setFilters({ ...filters, min_rating: e.target.value })} data-testid="filter-rating-select">
                    <option value="">Any rating</option>
                    <option value="4">4 stars & above</option>
                    <option value="3">3 stars & above</option>
                    <option value="2">2 stars & above</option>
                  </select>
                </div>
                <div>
                  <Label>Sort by</Label>
                  <select className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={filters.sort} onChange={(e) => setFilters({ ...filters, sort: e.target.value })} data-testid="filter-sort-select">
                    <option value="rating">Best rated</option>
                    <option value="fee">Lowest fee</option>
                    <option value="name">Name</option>
                  </select>
                </div>
                <Button className="w-full rounded-full bg-emerald-900 hover:bg-emerald-800" onClick={load} data-testid="apply-filters-btn">Apply filters</Button>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-3">
            <p className="mb-4 text-sm text-slate-500" data-testid="marketplace-count">
              {data?.count ?? 0} verified provider{data?.count === 1 ? "" : "s"} found
            </p>
            <div className="grid gap-5 sm:grid-cols-2">
              {(data?.providers || []).map((p) => (
                <article key={p.id} className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-transform hover:-translate-y-1" data-testid={`provider-card-${p.id}`}>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                    <BadgeCheck className="h-3.5 w-3.5" /> Verified · {p.registration_body}
                  </span>
                  <div className="mt-3 flex items-center gap-3">
                    <Avatar name={p.full_name} src={p.avatar_url} size="lg" testId={`provider-avatar-${p.id}`} />
                    <div className="min-w-0">
                      <h3 className="font-display text-lg font-bold text-slate-900">{p.full_name}</h3>
                      <p className="flex items-center gap-1 text-sm text-slate-500">
                        <span>{p.professional_category}</span>
                        {p.profession_tooltip && (
                          <InfoTip
                            headline={p.profession_tooltip.headline}
                            summary={p.profession_tooltip.summary}
                            testId={`provider-info-${p.id}`}
                          />
                        )}
                      </p>
                      {p.primary_specialty && (
                        <p className="text-xs font-medium text-emerald-800" data-testid={`provider-specialty-${p.id}`}>
                          {p.primary_specialty}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-2">
                    <RatingSummary average={p.rating_average} count={p.rating_count} testId={`provider-rating-${p.id}`} />
                  </div>
                  <p className="mt-3 line-clamp-2 text-sm text-slate-600">{p.bio}</p>
                  <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {p.state}</span>
                    <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {p.availability.accepting_bookings ? "Accepting bookings" : "Not accepting"}</span>
                  </div>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="font-display font-bold text-emerald-800">From {naira(p.min_fee_ngn)}</span>
                    <Button asChild size="sm" variant="outline" className="rounded-full">
                      <Link to={`/marketplace/${p.id}`} data-testid={`view-provider-${p.id}`}>View profile</Link>
                    </Button>
                  </div>
                </article>
              ))}
            </div>
            {data && data.count === 0 && (
              <p className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-8 text-center text-sm text-slate-500" data-testid="marketplace-empty">
                No verified providers match your filters.
              </p>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export function ProviderDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    api.get(`/api/marketplace/providers/${id}`).then((r) => setP(r.data)).catch((e) => toast.error(apiError(e)));
    api.get(`/api/marketplace/providers/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [id]);

  if (!p) return <DashboardLayout title="Provider profile"><Panel>Loading…</Panel></DashboardLayout>;

  return (
    <DashboardLayout
      title={p.full_name}
      subtitle={`${p.professional_category}${p.primary_specialty ? ` · ${p.primary_specialty}` : ""} · ${p.city}, ${p.state}`}
      badge={<StatusBadge status={p.verification_status} testId="provider-detail-status" />}
      nav={NAV}
    >
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Panel title="About" testId="provider-about-panel">
            {p.profession_tooltip && (
              <div className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50/60 p-4" data-testid="provider-detail-tooltip">
                <p className="text-sm font-bold text-emerald-900">{p.profession_tooltip.headline}</p>
                <p className="mt-1 text-xs leading-relaxed text-emerald-800">{p.profession_tooltip.summary}</p>
              </div>
            )}
            <p className="text-sm leading-relaxed text-slate-600">{p.bio}</p>
            <dl className="mt-6 grid gap-4 sm:grid-cols-2">
              {[
                ["Specialty", p.primary_specialty || "—"],
                ["Additional specialties", (p.additional_specialties || []).join(", ") || "—"],
                ["Licence status", p.license_status],
                ["Registration body", p.registration_body],
                ["Service coverage", (p.service_coverage_areas || []).join(", ") || "—"],
                ["Verification", p.verified ? "Verified by CARENEST" : "Not verified"],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-slate-400">{k}</dt>
                  <dd className="mt-1 text-sm text-slate-800">{v}</dd>
                </div>
              ))}
            </dl>
          </Panel>

          <Panel title="Services & professional fees" testId="provider-services-panel">
            <ul className="divide-y divide-slate-100">
              {p.services.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`provider-service-${s.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900">{s.name}</p>
                    <p className="text-xs text-slate-500">{s.category} · {s.duration_minutes} mins</p>
                  </div>
                  <span className="font-display font-bold text-emerald-800">{naira(s.fee_ngn)}</span>
                </li>
              ))}
              {p.services.length === 0 && <li className="py-4 text-sm text-slate-500">No services listed yet.</li>}
            </ul>
          </Panel>
        </div>

        <div className="space-y-6">
          <Panel title="Client ratings" testId="provider-reviews-panel">
            <RatingSummary average={reviews?.average} count={reviews?.count || 0} size="lg" testId="provider-detail-rating" />
            {reviews?.count > 0 && (
              <>
                <ul className="mt-4 space-y-1.5">
                  {[5, 4, 3, 2, 1].map((s) => (
                    <li key={s} className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="w-3">{s}</span>
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                        <span
                          className="block h-full rounded-full bg-amber-400"
                          style={{ width: `${((reviews.breakdown[String(s)] || 0) / reviews.count) * 100}%` }}
                        />
                      </span>
                      <span className="w-4 text-right">{reviews.breakdown[String(s)] || 0}</span>
                    </li>
                  ))}
                </ul>
                <ul className="mt-5 divide-y divide-slate-100" data-testid="provider-reviews-list">
                  {reviews.reviews.map((r) => (
                    <li key={r.id} className="py-3" data-testid={`provider-review-${r.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <Stars value={r.stars} />
                        <span className="text-[11px] text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                      {r.comment && <p className="mt-1.5 text-sm text-slate-600">{r.comment}</p>}
                      <p className="mt-1 text-xs text-slate-400">{r.client_name} · {r.service_name}</p>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Panel>

          <Panel title="Availability" testId="provider-availability-panel">
            <div className="flex flex-wrap gap-2">
              {p.availability.slots.map((s) => (
                <span key={s.day} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
                  {s.day} {s.start_time}–{s.end_time}
                </span>
              ))}
              {p.availability.slots.length === 0 && <span className="text-sm text-slate-500">No availability published.</span>}
            </div>
          </Panel>
          <Panel title="Book this provider" testId="provider-booking-panel">
            <p className="text-sm text-slate-600">
              Request a visit for one of your beneficiaries, then hold the fee in escrow until the visit is confirmed.
            </p>
            <Button asChild className="mt-4 rounded-full bg-emerald-900 hover:bg-emerald-800">
              <Link to="/bookings" data-testid="provider-book-cta">Request a visit</Link>
            </Button>
          </Panel>
        </div>
      </div>
    </DashboardLayout>
  );
}
